import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";

const PrivateRoute = ({ children, requiredRole, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [isValid, setIsValid] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const validateAccess = () => {
      if (loading) {
        setChecking(true);
        return;
      }

      if (!user) {
        setIsValid(false);
        setChecking(false);
        return;
      }
      
      // Get stored role from separate location
      const storedRole = localStorage.getItem('user_role');
      const userRole = user?.role;
      
      // Validate role integrity
      if (!storedRole || storedRole !== userRole) {
        console.error('Security: Role validation failed');
        localStorage.removeItem('user');
        localStorage.removeItem('user_role');
        localStorage.removeItem('auth_timestamp');
        setIsValid(false);
        setChecking(false);
        return;
      }
      
      // Check if requiredRole matches (single role)
      if (requiredRole && userRole !== requiredRole) {
        console.warn('Access denied: Role mismatch. Required:', requiredRole, 'Actual:', userRole);
        setIsValid(false);
        setChecking(false);
        return;
      }
      
      // Check if allowedRoles array contains user role
      if (allowedRoles && Array.isArray(allowedRoles)) {
        if (!allowedRoles.includes(userRole)) {
          console.warn('Access denied: Role not allowed. Allowed:', allowedRoles, 'Actual:', userRole);
          setIsValid(false);
          setChecking(false);
          return;
        }
      }
      
      // Check session timestamp
      const authTimestamp = localStorage.getItem('auth_timestamp');
      if (authTimestamp) {
        const timeDiff = Date.now() - parseInt(authTimestamp);
        if (timeDiff > 24 * 60 * 60 * 1000) {
          console.warn('Session expired');
          localStorage.clear();
          setIsValid(false);
          setChecking(false);
          return;
        }
      }
      
      setIsValid(true);
      setChecking(false);
    };

    validateAccess();
  }, [user, loading, requiredRole, allowedRoles]);

  if (checking) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Verifying security...</p>
      </div>
    );
  }

  if (!isValid) {
    // Clear potentially tampered data
    localStorage.removeItem('user');
    localStorage.removeItem('user_role');
    localStorage.removeItem('auth_timestamp');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

export default PrivateRoute;